import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EmbeddedViewRef,
  HostBinding,
  ViewEncapsulation,
  inject,
  input,
  InputSignal,
  ContentChildren,
  QueryList,
} from '@angular/core';
import {
  ButtonFill,
  ButtonSize,
  ComponentStatus,
} from '@lyceo/shared/util-models';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
  //eslint-disable-next-line @angular-eslint/component-selector
  selector: 'button[lyceoButton], a[lyceoButton]',
  imports: [],
  template: `<ng-content />`,
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ButtonComponent implements AfterContentChecked {
  private readonly cd: ChangeDetectorRef = inject(ChangeDetectorRef);

  public readonly disabled: InputSignal<boolean> = input<boolean>(false);
  public readonly tabIndex: InputSignal<number | null> = input<number | null>(
    null,
  );
  public readonly block: InputSignal<boolean> = input<boolean>(false);
  public readonly status: InputSignal<ComponentStatus> = input<ComponentStatus>(
    ComponentStatus.PRIMARY,
  );
  public readonly fill: InputSignal<ButtonFill> = input<ButtonFill>(
    ButtonFill.SOLID,
  );
  public readonly size: InputSignal<ButtonSize> = input<ButtonSize>(
    ButtonSize.MEDIUM,
  );

  @HostBinding('class.lyceo-button')
  public get lyceoButton(): boolean {
    return true;
  }

  @HostBinding('attr.disabled')
  public get isDisabled(): boolean | null {
    if (this.disabled()) {
      return true;
    }

    return null;
  }

  @HostBinding('attr.tabindex')
  public get tabbable(): string {
    if (this.disabled()) {
      return '-1';
    }

    const tabIndex: number | null = this.tabIndex();
    if (tabIndex === null) {
      return '0';
    }

    return tabIndex.toString();
  }

  @HostBinding('class.block')
  public get isBlock(): boolean {
    return this.block();
  }

  @HostBinding('class.status-primary')
  public get primary(): boolean {
    return this.status() === ComponentStatus.PRIMARY;
  }

  @HostBinding('class.status-secondary')
  public get secondary(): boolean {
    return this.status() === ComponentStatus.SECONDARY;
  }

  @HostBinding('class.status-success')
  public get success(): boolean {
    return this.status() === ComponentStatus.SUCCESS;
  }

  @HostBinding('class.status-warning')
  public get warning(): boolean {
    return this.status() === ComponentStatus.WARNING;
  }

  @HostBinding('class.status-danger')
  public get danger(): boolean {
    return this.status() === ComponentStatus.DANGER;
  }

  @HostBinding('class.fill-clear')
  public get clear(): boolean {
    return this.fill() === ButtonFill.CLEAR;
  }

  @HostBinding('class.fill-outline')
  public get outline(): boolean {
    return this.fill() === ButtonFill.OUTLINE;
  }

  @HostBinding('class.fill-solid')
  public get solid(): boolean {
    return this.fill() === ButtonFill.SOLID;
  }

  @HostBinding('class.size-small')
  public get small(): boolean {
    return this.size() === ButtonSize.SMALL;
  }

  @HostBinding('class.size-medium')
  public get medium(): boolean {
    return this.size() === ButtonSize.MEDIUM;
  }

  @HostBinding('class.icon-start') public iconLeft: boolean = false;

  @HostBinding('class.icon-end') public iconRight: boolean = false;

  @ContentChildren(FaIconComponent, { read: ElementRef })
  public icons!: QueryList<ElementRef>;

  public ngAfterContentChecked(): void {
    const firstNode: Node = this.nodes[0];
    const lastNode: Node = this.nodes[this.nodes.length - 1];

    this.iconLeft = this.isIconExist(firstNode);
    this.iconRight = this.isIconExist(lastNode);
  }

  private get nodes(): Node[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.cd as EmbeddedViewRef<any>).rootNodes.filter(
      (child: Node) => child.nodeType !== Node.COMMENT_NODE,
    );
  }

  private isIconExist(node: Node): boolean {
    return this.icons.some((item) => item.nativeElement === node);
  }
}
